// Výsledek leadu na kartě kontaktu (hlášení #139, Kristina; mockup schválený 7. 10. 2026).
// Owner, manažer a CSM tu zapíšou, jak lead dopadl, ve stejné řeči jako Notion stage:
// Deal (založit klientku), Prohra (důvod, poznámka, případně připomínka „ozvat se znovu“),
// Jen Deal a Prohra (7. 10. 2026, Láďa): NQlead a vrácení k volání dělá jen sales při hovoru. Sales tenhle blok nevidí,
// má svoje akce v hovoru. Vedle nadpisu je aktuální stage a kdo ji naposled změnil.
import React, { useEffect, useState } from "react";
import { XCircle, Handshake } from "lucide-react";
import { Modal, HlavickaOkna, Field } from "../ui.jsx";
import { toast } from "../Toast.jsx";
import { confirmDialog } from "../confirmHost.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import { salesPost, kdyUdalost, dnesIso, addDays, dm } from "./salesApi.js";
import DatePicker from "../DatePicker.jsx";
import { Oznameni, Cipy } from "./salesUi.jsx";
import ZalozitKlientku, { dealMozny, nactiRezim } from "./ZalozitKlientku.jsx";
import { StagePilulka } from "../LeadyPrehled.jsx";

const ROLE = new Set(["owner", "manager", "csm"]);
const DUVODY = [
  { key: "cena", label: "Cena" }, { key: "dojezd", label: "Dojezd" }, { key: "trener", label: "Nesedl trenér" },
  { key: "zdravi", label: "Zdravotní důvody" }, { key: "online", label: "Chce jen online" }, { key: "konkurence", label: "Vybrala konkurenci" },
  { key: "nereaguje", label: "Nereaguje" }, { key: "jiny", label: "Jiný důvod" },
];
// Připomínka „ozvat se znovu": rychlé volby + přesné datum v kalendáři (7. 10. 2026, Láďa).
const OZVAT = [{ key: "ne", label: "Ne" }, { key: "3m", label: "Za 3 měsíce" }, { key: "6m", label: "Za 6 měsíců" }, { key: "rok", label: "Za rok" }, { key: "vlastni", label: "Vlastní datum" }];
function zaMesice(n) { const d = dnesIso(); const [y, m, den] = d.split("-").map(Number); return new Date(Date.UTC(y, m - 1 + n, den)).toISOString().slice(0, 10); }
function datumVolby(k) { return k === "3m" ? zaMesice(3) : k === "6m" ? zaMesice(6) : k === "rok" ? zaMesice(12) : k === "mesic" ? zaMesice(1) : dnesIso(); }

export function ProhraDialog({ lead, open, onClose, onHotovo }) {
  const [duvod, setDuvod] = useState("");
  const [pozn, setPozn] = useState("");
  const [ozvat, setOzvat] = useState("ne");
  const [datum, setDatum] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setDuvod(""); setPozn(""); setOzvat("ne"); setDatum(""); setChyba(""); } }, [open]);
  const zmenOzvat = (k) => { const v = k || "ne"; setOzvat(v); setDatum(v === "ne" ? "" : v === "vlastni" ? (datum || addDays(dnesIso(), 30)) : datumVolby(v)); };
  const zk = (lead?.zkusebni || []).find((z) => z.stav === "aktivni");
  const uloz = async () => {
    if (!duvod) { setChyba("Vyber důvod."); return; }
    const duvodText = (DUVODY.find((d) => d.key === duvod) || {}).label || duvod;
    const ok = await confirmDialog({
      title: `Opravdu zapsat prohru u ${lead.jmeno}?`,
      description: `Důvod: ${duvodText}. Lead dostane stage Prohra a zmizí sales z volání.${ozvat !== "ne" && datum ? ` ${dm(datum)} se sám vrátí sales do Plánu.` : ""}`,
      confirmLabel: "Ano, zapsat prohru", variant: "danger",
    });
    if (!ok) return;
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("prohra", { id: lead.id, duvod, poznamka: pozn, ozvat_dne: ozvat === "ne" ? null : datum });
      toast(ozvat !== "ne" && datum ? `Prohra zapsaná, ${dm(datum)} se lead vrátí sales do volání.` : "Prohra zapsaná.", "success");
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  if (!lead) return null;
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={XCircle} nazev="Prohra" popis={lead.jmeno} onClose={onClose} />}
      footer={<>
        {chyba && <div className="basis-full"><Oznameni ton="problem">{chyba}</Oznameni></div>}
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-danger" onClick={uloz} disabled={busy || !duvod || (ozvat !== "ne" && !datum)} aria-busy={busy}>{busy ? "Zapisuju…" : "Zapsat prohru"}</button>
      </>}>
      <div className="space-y-4">
        <Oznameni ton="problem">Lead dostane stage Prohra a zmizí z Volání{zk ? ". Domluvený zkušební trénink zůstane v historii a faktura se nestornuje, případně ho zruš zvlášť" : ""}.</Oznameni>
        <div><span className="label">Důvod</span><Cipy moznosti={DUVODY} value={duvod} onChange={(k) => { setDuvod(k || ""); setChyba(""); }} /></div>
        <Field label="Poznámka (nepovinné)">
          <textarea className="field min-h-[72px]" value={pozn} onChange={(e) => setPozn(e.target.value)} placeholder="Např. 2× týdně je pro ni drahé, zkusí to na jaře" />
        </Field>
        <div><span className="label">Připomenout sales, ozvat se znovu</span><Cipy moznosti={OZVAT} value={ozvat} onChange={zmenOzvat} /></div>
        {ozvat !== "ne" && (
          <div>
            <span className="label">Datum</span>
            <DatePicker value={datum} min={addDays(dnesIso(), 1)} onChange={(d) => { setDatum(d); setOzvat("vlastni"); }} />
            <div className="mt-1.5 text-[12px] text-slate-500">V ten den se lead sám vrátí sales do Plánu volání s poznámkou, proč prohrál.</div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function VysledekLeadu({ k, onZmena }) {
  const role = String(getCurrentUser()?.role || "").toLowerCase();
  const [rezim, setRezim] = useState(null);
  const [dialog, setDialog] = useState(null);
  useEffect(() => { let zr = false; nactiRezim().then((r) => !zr && setRezim(r)); return () => { zr = true; }; }, []);
  if (!ROLE.has(role) || !k || k.blacklist) return null;

  const uzKlientka = k.klientka?.stav === "Aktivní";
  const deal = rezim ? dealMozny(k, rezim) : false;
  const posledni = (k.historie || [])[0];
  const tl = "flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-xl px-2 text-[13px] font-bold transition-colors";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Výsledek <span className="font-semibold normal-case tracking-normal text-slate-400">(owner, manažer, CSM)</span></div>
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <span>Stage:</span><StagePilulka stage={k.stage} />
          {posledni && <><span className="h-3 w-px bg-slate-300" aria-hidden="true" /><span>{posledni.kdo || "automat"}, {kdyUdalost(posledni.kdy).den}</span></>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {deal
          ? <ZalozitKlientku k={k} kompaktni onHotovo={() => onZmena?.()} />
          : <button type="button" disabled className={`${tl} cursor-not-allowed border border-slate-200 bg-slate-50 text-slate-400`} title={uzKlientka ? "Už je klientka" : "Deal jde zapsat po proběhlém zkušebním tréninku"}><Handshake className="h-4 w-4" />Deal</button>}
        <button type="button" disabled={k.stav === "neuzavreno"} className={`${tl} border-2 border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50`} onClick={() => setDialog("prohra")}><XCircle className="h-4 w-4" />Prohra</button>
      </div>
      <div className="mt-2 text-[12px] text-slate-500">Prohra se zeptá na důvod, před zápisem se ještě potvrdí a zapíše do historie, kdo a proč. Deal založí klientku, lead zmizí z Leady.</div>
      {/* 1. 11. 2026 (zadání 5): po prohře karta hned nabídne email s nabídkou bootcampu a Klubu (NeuzavrenoEmailBlok). */}
      <ProhraDialog lead={k} open={dialog === "prohra"} onClose={() => setDialog(null)} onHotovo={() => { setDialog(null); onZmena?.({ prohra: true }); }} />
    </div>
  );
}
